import { SpinnerGapIcon, FileIcon, FilePdfIcon, FileDocIcon, WarningCircleIcon, XIcon } from '@phosphor-icons/react';
import Tooltip from '@/components/ui/Tooltip';

export interface FileItemPillProps {
  filename: string;
  /** Upload status, or undefined for completed files from history. */
  status?: 'uploading' | 'scanning' | 'processing' | 'complete' | 'error';
  /** Upload progress percentage (0–100). Only meaningful when status is 'uploading'. */
  uploadProgress?: number;
  /** Error message to show in tooltip. */
  errorMessage?: string;
  /** Context percent label (e.g. "45%"). Shown when exceedsContextLimit is true. */
  contextPercent?: number | null;
  exceedsContextLimit?: boolean;
  /** Called when the remove button is clicked. If not provided, no remove button is shown. */
  onRemove?: () => void;
  /** Called when the pill is clicked (e.g. for download). */
  onClick?: () => void;
  /** Whether the pill should be disabled (e.g. during processing in a message). */
  disabled?: boolean;
  /** Visual variant. 'default' has no border; 'outlined' adds a border like the outline Button. */
  variant?: 'default' | 'outlined';
}

export default function FileItemPill({
  filename,
  status,
  uploadProgress = 0,
  errorMessage,
  contextPercent,
  exceedsContextLimit,
  onRemove,
  onClick,
  disabled,
  variant = 'default'
}: FileItemPillProps) {
  const isUploading = status === 'uploading';
  const isScanning = status === 'scanning';
  const isProcessing = status === 'processing';
  const hasError = status === 'error';
  const isActive = isUploading || isScanning || isProcessing;

  const statusLabel = hasError
    ? `Failed to upload ${filename}`
    : isUploading
      ? `Uploading ${filename}`
      : isScanning
        ? `Scanning ${filename}`
        : isProcessing
          ? `Processing ${filename}`
          : `Chat file ${filename}`;

  const isPdf = filename.toLowerCase().endsWith('.pdf');
  const isDoc = filename.toLowerCase().endsWith('.docx');

  const truncatedName = filename.length > 20 ? filename.substring(0, 17) + '...' : filename;

  const icon = isActive ? (
    <SpinnerGapIcon size={16} className="relative z-10 animate-spin text-gray-500" />
  ) : hasError ? (
    <WarningCircleIcon size={16} className="text-red-500" />
  ) : isPdf ? (
    <FilePdfIcon size={16} weight="light" className="relative z-10" />
  ) : isDoc ? (
    <FileDocIcon size={16} weight="light" className="relative z-10" />
  ) : (
    <FileIcon size={16} weight="light" className="relative z-10" />
  );

  const pill = (
    <div
      role="status"
      aria-label={statusLabel}
      aria-busy={isActive}
      className={`relative overflow-hidden flex items-center gap-2 rounded-md text-sm font-medium text-gray-800 px-2 py-1 ${
        hasError
          ? 'border border-red-300 bg-gray-100'
          : variant === 'outlined'
            ? 'border-2 border-border-default bg-transparent'
            : 'bg-gray-100'
      } ${onClick && !disabled ? 'cursor-pointer hover:bg-gray-200' : ''} ${disabled ? 'cursor-not-allowed' : ''}`}
      onClick={!disabled ? onClick : undefined}
    >
      {/* Upload progress bar background */}
      {isUploading && (
        <div
          className="absolute inset-0 bg-blue-100 transition-[width] duration-300 ease-out"
          style={{ width: `${uploadProgress}%` }}
        />
      )}
      {icon}
      <span className="relative z-10 max-w-[150px] truncate" title={filename}>
        {truncatedName}
      </span>
      {isUploading && (
        <span className="relative z-10 text-xs text-gray-400">
          {uploadProgress < 100 ? `${uploadProgress}%` : 'Uploading…'}
        </span>
      )}
      {isScanning && <span className="relative z-10 text-xs text-gray-400">Scanning…</span>}
      {isProcessing && <span className="relative z-10 text-xs text-gray-400">Processing…</span>}
      {exceedsContextLimit && contextPercent != null && (
        <Tooltip
          title={
            contextPercent > 100
              ? `This file uses ${contextPercent}% of CompliMind chat context limit and is too large to process on its own.`
              : `This file uses ${contextPercent}% of CompliMind chat context limit.`
          }
          enterDelay={0}
          leaveDelay={0}
        >
          <span className={`relative z-10 text-xs ${contextPercent > 100 ? 'text-red-500' : 'text-gray-400'}`}>
            {contextPercent}%
          </span>
        </Tooltip>
      )}
      {onRemove && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="relative z-10 text-gray-500 hover:text-gray-700"
          aria-label={`Remove ${filename}`}
        >
          <XIcon size={14} />
        </button>
      )}
    </div>
  );

  if (hasError && errorMessage) {
    return (
      <Tooltip title={errorMessage} enterDelay={0} leaveDelay={0}>
        {pill}
      </Tooltip>
    );
  }

  if (disabled && !onClick) {
    return (
      <Tooltip title="You don't have access to this user-uploaded file.">
        <span className="cursor-not-allowed">{pill}</span>
      </Tooltip>
    );
  }

  return pill;
}
